import React, {Fragment, useEffect} from 'react';

import {skipToken} from '@reduxjs/toolkit/query';
import {toInteger} from 'lodash';
import throttle from 'lodash/throttle';
import {useSelector} from 'react-redux';
import {withRouter, type WithRouterProps} from 'react-router';

import {PendingAcceptanceInfoBar} from 'app/acceptance/components/PendingAcceptanceInfoBar';
import {PageWithRightSidebar} from 'app/components/general/DetailPage/PageWithRightSidebar';
import {
  type GenericDocumentPropsForDetailPage,
  RenderWhenDocumentIsGatheredDetailPage,
} from 'app/components/general/DetailPage/RenderWhenDocumentIsGathered';
import {StickyDiagramForProcedures} from 'app/components/general/DetailPage/RightSidebar/StickyDiagramSidebar';
import {StickyComments} from 'app/components/general/DetailPage/RightSidebar/StickyDocumentCommentsSidebar';
import {GenericDocumentActionButtons} from 'app/components/general/GenericDocumentActionButtons';
import {ListMessage} from 'app/components/general/ListMessage';
import VersionInfoBar from 'app/components/general/VersionInfoBar';
import {
  VersionDiffer,
  type VersionDiffType,
} from 'app/components/VersionDiffer';
import {ProcedureDetailTour} from 'app/current-user/components/tour/ProcedureDetailTour';
import {ViewStepsModeTitle} from 'app/general/detailpage/ViewStepsModeTitle';
import {AttachTooltip} from 'app/general/overlays';
import {BreadcrumbItemStructure} from 'app/general/structures/BreadcrumbItemStructure';
import {LargeContentAlertBox} from 'app/general/structures/document-alerts/LargeContentAlertBox';
import {showNoApprovedVersionsAlertBoxIfNotApproved} from 'app/general/structures/document-alerts/NoApprovedVersionsAlertBox';
import {VideoManagerProvider} from 'app/general/VideoManagerProvider';
import {useLanesForDocument} from 'app/lanes/api';
import {DocumentPresenceMembers} from 'app/presence/components/DocumentPresenceMembers';
import {toggleProcedureAdvancedMode} from 'app/procedures/actions';
import {
  useProcedureByHashid,
  useProcedureVersionById,
  useProcedureVersions,
} from 'app/procedures/api';
import {CollapsedStepsState} from 'app/procedures/components/CollapsedStepsState';
import {ProcedureHeaderOptionsDropdown} from 'app/procedures/components/ProcedureHeaderOptionsDropdown';
import {StepViewFieldWrapper} from 'app/procedures/components/step-form-fields/StepViewFieldWrapper';
import {
  StyledCheckboxButton,
  StyledStepsListDIV,
} from 'app/procedures/components/structures';
import {ViewableAutomation} from 'app/procedures/components/ViewableAutomation';
import {ViewableChoices} from 'app/procedures/components/ViewableChoices';
import {ViewProcedureMetaData} from 'app/procedures/components/ViewProcedureMetaData';
import {getMemoizedProcedureOrphanSteps} from 'app/procedures/selectors';
import {useProcessByHashid} from 'app/process/api';
import {ReviewReminderWarning} from 'app/review/components/ReviewReminderWarning';
import {useAccountSettingsState} from 'app/settings/api';
import {SignoffForm} from 'app/signoffs/components/SignoffForm';
import {SignoffRequiredInfoBar} from 'app/signoffs/components/SignoffRequiredInfoBar';
import {NextStepLinkItem} from 'app/steps/components/NextStepLinkItem';
import {StepLaneItem} from 'app/steps/components/StepLaneItem';
import {sweetAPI} from 'app/store/sweet-api';
import {
  allRelatedMatch,
  modelIdMatch,
  runWhenAny,
  useModelChangeSubscription,
} from 'app/subscriptions';
import {NOTIFICATION_DEFAULT_TIMEOUT, showNotification} from 'app/toasts/slice';
import {scrollIntoStep} from 'app/utils/browser';
import {getProcedureName} from 'app/utils/fallbacks';
import {gt} from 'app/utils/i18n';
import {useAsyncActions} from 'app/utils/redux';
import {
  ACCEPTANCE_RTK_TAGS,
  COMMENT_TAG,
  LANE_RTK_TAGS,
  PROCEDURE_RTK_TAGS,
} from 'app/utils/rtk-tags';
import {isRightToLeftWrapper} from 'app/utils/rtl';
import {getComparedVersion} from 'app/utils/showdiff';
import {isLastVersion} from 'app/version/utils';

import {ProcedurePageSidebar} from './ProcedurePageSidebar';
import {ProcedureStep} from './ProcedureStep';

import type {
  DetailPageBaseProps,
  DetailPageRouterProps,
} from 'app/components/general/DetailPage/types';
import type {
  ProcedureHashidSerializedType,
  ProcedureVersionSerializedType,
} from 'sweet-types';

export default function ProcedureDetailPage({
  route,
}: DetailPageBaseProps<ProcedureHashidSerializedType>) {
  return (
    <RenderWhenDocumentIsGatheredDetailPage
      route={route}
      useHashidHook={useProcedureByHashid}
      useSelectedVersionHook={useProcedureVersionById}
      useVersionsHook={useProcedureVersions}
      DetailPage={ProcedureDetailPageWithRouter}
    />
  );
}

const rightSidebarWidgets = [StickyDiagramForProcedures, StickyComments];
function BaseProcedureDetailPage({
  route,
  object: procedure,
  version,
  versions,
  refetch,
  location,
  params,
}: GenericDocumentPropsForDetailPage<ProcedureHashidSerializedType> &
  DetailPageRouterProps) {
  const CurrentLeftSidebar = route.props?.CurrentLeftSidebar;
  const CurrentRightSidebar = route.props?.CurrentRightSidebar;
  const {versionid: versionId} = params;

  useLanesForDocument({
    object_ids: procedure.id,
    content_type: procedure.content_type,
  });

  const canDisplayVersionInterface = versionId != null && version != null;

  const deletingDocument = useSelector(
    (state) => state.toasts.isDeletingDocument
  );

  const advancedMode = useSelector((state) =>
    versionId == null
      ? state.proceduresPage.get('procedureAdvancedMode')
      : false
  );

  const actions = useAsyncActions({
    toggleProcedureAdvancedMode,
    invalidateTags: sweetAPI.util.invalidateTags,
  });

  useModelChangeSubscription(
    (message) => {
      if (deletingDocument) {
        return;
      }

      const expectedRelated = {
        content_type: 'procedure',
        id: procedure.id,
      };

      runWhenAny(
        message,
        [
          modelIdMatch('procedure', procedure.id),
          allRelatedMatch('procedureversion', expectedRelated),
          allRelatedMatch('comment', expectedRelated),
          allRelatedMatch('review', expectedRelated),
        ],
        () => {
          actions.invalidateTags([
            // @ts-expect-error we inject tags on the API after it's created so TS doesn't know any tag type
            {type: LANE_RTK_TAGS.TAGS, id: 'list'},
            // @ts-expect-error we inject tags on the API after it's created so TS doesn't know any tag type
            {type: PROCEDURE_RTK_TAGS.HASHID, id: procedure.url},
            // @ts-expect-error we inject tags on the API after it's created so TS doesn't know any tag type
            {type: PROCEDURE_RTK_TAGS.TAGS, id: 'list'},
            // @ts-expect-error we inject tags on the API after it's created so TS doesn't know any tag type
            {type: ACCEPTANCE_RTK_TAGS.TAGS, id: 'list'},
          ]);
        }
      );
      runWhenAny(message, [allRelatedMatch('comment', expectedRelated)], () =>
        // @ts-expect-error we inject tags on the API after it's created so TS doesn't know any tag type
        actions.invalidateTags([{type: COMMENT_TAG, id: 'list'}])
      );

      if (canDisplayVersionInterface) {
        const expectedRelatedVersion = {
          content_type: 'procedureversion',
          id: version.id,
        };
        runWhenAny(
          message,
          [allRelatedMatch('acceptance', expectedRelatedVersion)],
          () => {
            actions.invalidateTags([
              // @ts-expect-error we inject tags on the API after it's created so TS doesn't know any tag type
              {type: ACCEPTANCE_RTK_TAGS.TAGS, id: 'list'},
            ]);
          }
        );
      }
    },
    [deletingDocument, version]
  );

  const {account_id} = useAccountSettingsState().data!;

  const procedureAndUserInSameAccount = account_id === procedure.account_id;

  const editableContent = version || procedure;

  const previousVersion =
    versionId != null
      ? getComparedVersion(
          editableContent.url,
          versions,
          location.query['selected-version-group']
        ) || null
      : null;

  return (
    <CollapsedStepsState stepsIds={editableContent.content.map(({id}) => id)}>
      {({
        collapsedSteps,
        numCollapsedSteps,
        toggleAllStepsCollapse,
        toggleStepCollapse,
      }) => (
        <VersionDiffer
          location={location}
          content={editableContent}
          previousVersion={previousVersion}
          versionAvailable={canDisplayVersionInterface}
        >
          {(contentWithDiff, showingDiff, toggleDiff) => {
            const actionButtonsProps = {
              contentType: procedure.content_type,
              document: procedure,
              canChange: procedure.can_change,
              additionalContent: () => (
                <ProcedureHeaderOptionsDropdown
                  advancedModeEnabled={advancedMode}
                  toggleAdvancedMode={actions.toggleProcedureAdvancedMode}
                  numCollapsedSteps={numCollapsedSteps}
                  onToggleAllStepsCollapse={toggleAllStepsCollapse}
                  stepsLength={contentWithDiff.content.length}
                />
              ),
            };
            return (
              <PageWithRightSidebar
                context={'view'}
                useHashidHook={useProcedureByHashid}
                useSelectedVersionHook={useProcedureVersionById}
                LeftSidebar={ProcedurePageSidebar}
                CurrentLeftSidebar={CurrentLeftSidebar}
                CurrentRightSidebar={CurrentRightSidebar}
                rightSidebarPanels={rightSidebarWidgets}
                breadcrumbProps={{
                  procedureId: procedure.id,
                  docName: getProcedureName(procedure.name),
                  link: procedure.html_url + location.search,
                  version,
                }}
                actionArea={
                  canDisplayVersionInterface ? (
                    <GenericDocumentActionButtons
                      version={version}
                      isLastVersion={isLastVersion(version, versions)}
                      pageContext={'view-version'}
                      {...actionButtonsProps}
                    />
                  ) : (
                    <GenericDocumentActionButtons
                      pageContext={'view-published'}
                      {...actionButtonsProps}
                    />
                  )
                }
                versionbar={
                  <>
                    {canDisplayVersionInterface && (
                      <VersionInfoBar
                        showingDiff={showingDiff}
                        toggleDiff={toggleDiff}
                        objectTypeName={gt('procedure')}
                        version={version}
                        className={'sidebar-visible'}
                      />
                    )}
                    {!(CurrentLeftSidebar?.sidebarName === 'showAccept') && (
                      <PendingAcceptanceInfoBar
                        document={procedure}
                        location={location}
                        openedVersion={version}
                      />
                    )}
                    {versionId == null && (
                      <SignoffRequiredInfoBar
                        contentType="procedure"
                        objectId={procedure.id}
                        viewChangesURL={procedure.current_version.html_url}
                      />
                    )}
                  </>
                }
              >
                <DocumentPresenceMembers
                  contentType="procedure"
                  objectId={procedure.id}
                  pageContext="view"
                  contexts={['view', 'edit', 'task']}
                />
                <VideoManagerProvider>
                  <ViewProcedureMetaData
                    content={contentWithDiff}
                    procedure={procedure}
                    versionId={toInteger(
                      versionId ?? procedure.current_version.id
                    )}
                    procedureAndUserInSameAccount={
                      procedureAndUserInSameAccount
                    }
                    warningComponent={
                      <Fragment>
                        {showNoApprovedVersionsAlertBoxIfNotApproved(
                          'procedure',
                          procedure.approved_at
                        )}
                        <LargeContentAlertBox
                          contentType="procedure"
                          id={procedure.id}
                          isLarge={contentWithDiff.is_large}
                        />
                        <ReviewReminderWarning
                          contentType="procedure"
                          versionId={procedure.current_version.id}
                          versionContentType="procedureversion"
                          canReview={procedure.can_edit}
                          onReview={refetch}
                          reviewDate={procedure.next_review_at}
                        />
                      </Fragment>
                    }
                    advancedModeEnabled={advancedMode}
                  />
                  <ProcedureSteps
                    procedure={procedure}
                    editableContent={editableContent}
                    numCollapsedSteps={numCollapsedSteps}
                    toggleAllStepsCollapse={toggleAllStepsCollapse}
                    contentWithDiff={contentWithDiff}
                    versionId={versionId}
                    collapsedSteps={collapsedSteps}
                    toggleStepCollapse={toggleStepCollapse}
                    procedureVersion={version}
                  />
                </VideoManagerProvider>
                {version == null && (
                  <ProcedureDetailTour
                    additionalData={contentWithDiff.content.length}
                  />
                )}
              </PageWithRightSidebar>
            );
          }}
        </VersionDiffer>
      )}
    </CollapsedStepsState>
  );
}

const ProcedureDetailPageWithRouter = withRouter(BaseProcedureDetailPage);

interface BreadcrumbProps extends WithRouterProps {
  active: boolean;
  icon: string;
}

export const MaybeProcedureProcessContainerBreadcrumb = withRouter(
  ({location, active, icon}: BreadcrumbProps) => {
    const {query} = location;
    const {data: process} = useProcessByHashid(
      query.gid == null ? skipToken : query.gid,
      {refetchOnMountOrArgChange: false}
    );

    return process != null ? (
      <BreadcrumbItemStructure
        shrinkText={true}
        active={active}
        icon={icon}
        text={process.name}
        link={process.html_url}
      />
    ) : null;
  }
);

interface ProcedureStepsProps {
  procedure: ProcedureHashidSerializedType;
  editableContent:
    | ProcedureHashidSerializedType
    | ProcedureVersionSerializedType;
  numCollapsedSteps: number;
  toggleAllStepsCollapse: (collapse: boolean) => void;
  contentWithDiff: VersionDiffType;
  versionId?: string;
  collapsedSteps: Record<string, boolean>;
  toggleStepCollapse: (id: string, collapse: boolean | null) => void;
  procedureVersion: ProcedureVersionSerializedType | undefined;
}

function ProcedureSteps({
  procedure,
  editableContent,
  numCollapsedSteps,
  toggleAllStepsCollapse,
  contentWithDiff,
  versionId,
  collapsedSteps,
  toggleStepCollapse,
  procedureVersion,
}: ProcedureStepsProps) {
  const orphans = getMemoizedProcedureOrphanSteps(
    editableContent.content,
    procedure.steps_blocked_at
  );

  const throttledShowNotification = throttle(
    (message: string) => {
      showNotification(message, 'info');
    },
    NOTIFICATION_DEFAULT_TIMEOUT,
    {
      leading: true,
      trailing: false,
    }
  );

  useEffect(() => {
    if (location.hash) {
      setTimeout(() => scrollIntoStep(location.hash.replace('#', '')));
    }
  }, []);

  return (
    <StyledStepsListDIV className="js-document-body container pt-0">
      <div className="row">
        <div className="col pt-4 col-12">
          <ViewStepsModeTitle
            numCollapsedSteps={numCollapsedSteps}
            toggleAllStepsCollapse={toggleAllStepsCollapse}
          />
          <ol className="steps">
            {contentWithDiff.content.map((step, index) => (
              <li key={index} id={step.id}>
                <ProcedureStep
                  orphan={orphans.has(step.id)}
                  index={index}
                  contentType={'procedureversion'}
                  step={step}
                  showCopyLink={versionId == null}
                  collapsed={collapsedSteps[step.id]}
                  onToggleCollapse={toggleStepCollapse}
                  objectId={procedure.id}
                  versionId={
                    procedureVersion?.id || procedure.current_version.id
                  }
                  actionButtons={
                    versionId == null &&
                    (!step.type || step.type === 'content') ? (
                      <AttachTooltip
                        placement="left"
                        tooltipContent={gt(
                          'Start this procedure to begin checking off steps.'
                        )}
                      >
                        {(handlers) => (
                          <span
                            className={`${
                              isRightToLeftWrapper(step.title || '')
                                ? 'float-left'
                                : 'float-right'
                            } d-inline-block`}
                            {...handlers}
                          >
                            <StyledCheckboxButton
                              disabled={true}
                              isChecked={false}
                              onClick={() => null}
                              fixedWidth={true}
                            />
                          </span>
                        )}
                      </AttachTooltip>
                    ) : null
                  }
                  fieldInputs={
                    step.type === 'content' ? (
                      <StepViewFieldWrapper
                        fields={step.fields}
                        showNotification={throttledShowNotification}
                      />
                    ) : null
                  }
                  choicesList={
                    step.type === 'decision' ? (
                      <ViewableChoices
                        choices={step.choices}
                        steps={editableContent.content}
                        edit={false}
                      />
                    ) : null
                  }
                  automation={
                    step.type === 'automation' ? (
                      <ViewableAutomation step={step} />
                    ) : null
                  }
                  previous={
                    <StepLaneItem
                      stepLaneId={editableContent.lanes[step.id]}
                      laneDiff={contentWithDiff.lanesDiff?.[step.id]}
                    />
                  }
                  next={
                    <NextStepLinkItem
                      step={step}
                      steps={editableContent.content}
                    />
                  }
                />
              </li>
            ))}
          </ol>

          {contentWithDiff.content.length === 0 && (
            <ListMessage gray={true}>
              {gt('No steps for this procedure')}
            </ListMessage>
          )}

          {versionId == null && (
            <SignoffForm
              objectId={procedure.id}
              contentType="procedure"
              versionId={procedure.current_version.id}
              versionContentType="procedureversion"
              objectName={procedure.name}
            />
          )}
        </div>
      </div>
    </StyledStepsListDIV>
  );
}
